import React, { useEffect, useState } from 'react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Loader2, Link2 } from 'lucide-react';
import { toast } from 'sonner';

const BotKnowledgePanel = ({ botId, token }) => {
  const [sources, setSources] = useState([]);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const fetchSources = async () => {
      if (!botId || !token) return;
      setLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/bots/${botId}/knowledge-sources`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const data = await res.json().catch(() => []);
        if (res.ok) {
          setSources(data || []);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    fetchSources();
  }, [botId, token]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!url || !botId || !token) return;
    setAdding(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/bots/${botId}/knowledge-sources`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ url }),
        }
      );
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.detail || 'Failed to add knowledge source');
      }
      setSources((prev) => [data, ...prev]);
      setUrl('');
      toast.success('Knowledge source added');
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to add knowledge source');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center">
            <Link2 className="w-3.5 h-3.5" />
          </div>
          <div>
            <h2 className="text-sm font-semibold">Knowledge Sources</h2>
            <p className="text-[11px] text-zinc-500">URLs this bot can use as context</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleAdd} className="flex gap-2 mt-1">
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com/docs"
          className="h-8 text-xs bg-zinc-900 border-zinc-700 focus:border-sky-500"
        />
        <Button
          type="submit"
          size="sm"
          className="h-8 px-3 text-xs bg-sky-600 hover:bg-sky-500"
          disabled={adding}
        >
          {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add URL'}
        </Button>
      </form>

      <div className="mt-2 max-h-40 overflow-y-auto space-y-1 pr-1">
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading sources...
          </div>
        ) : sources.length === 0 ? (
          <p className="text-[11px] text-zinc-500">No URLs added yet. Add a page so the bot can learn from it.</p>
        ) : (
          sources.map((s) => (
            <div
              key={s.id}
              className="border border-zinc-800 rounded-lg px-2 py-1.5 text-[11px] text-zinc-300 bg-zinc-900/60"
            >
              <div className="flex items-center justify-between mb-0.5">
                <span className="truncate max-w-[220px]" title={s.url}>
                  {s.url}
                </span>
                <span className="uppercase text-[9px] tracking-wide text-zinc-500">
                  {s.status || 'unknown'}
                </span>
              </div>
              {s.last_crawled_at && (
                <p className="text-[9px] text-zinc-500">
                  Last crawled: {new Date(s.last_crawled_at).toLocaleString()}
                </p>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BotKnowledgePanel;
